import { useQuery } from "@tanstack/react-query";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";

import * as clientsApi from "../clients.api";
import { IUsePartnerSelect } from "./partner-select.hooks.interface";
import {
  isPartnerSelectIntent,
  TPartnerSelectIntent,
} from "./partner-select.types";

function destinationForIntent(
  intent: TPartnerSelectIntent,
  partnerId: string
): { path: string; state?: { openClientId: string } } {
  switch (intent) {
    case "link-users":
      return { path: "/clients", state: { openClientId: partnerId } };
    case "projects":
      return { path: `/clients/${partnerId}/projects/list` };
    case "campaigns":
      return { path: `/clients/${partnerId}/campaigns` };
    case "whatsapp":
      return { path: `/clients/${partnerId}/whatsapp` };
    default:
      return { path: "/clients" };
  }
}

export function usePartnerSelect(): IUsePartnerSelect {
  const { intent: intentParam } = useParams<{ intent: string }>();
  const navigate = useNavigate();

  const intent = React.useMemo(() => {
    if (!intentParam || !isPartnerSelectIntent(intentParam)) {
      return null;
    }
    return intentParam;
  }, [intentParam]);

  React.useEffect(() => {
    if (intentParam && !isPartnerSelectIntent(intentParam)) {
      navigate("/clients", { replace: true });
    }
  }, [intentParam, navigate]);

  const {
    data: clients,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["clients", "partner-select"],
    queryFn: clientsApi.getAllClients,
    enabled: intent !== null,
  });

  const onSelectPartner = React.useCallback(
    (partnerId: string) => {
      if (!intent) {
        return;
      }
      const dest = destinationForIntent(intent, partnerId);
      if (dest.state) {
        navigate(dest.path, { state: dest.state });
      } else {
        navigate(dest.path);
      }
    },
    [intent, navigate]
  );

  return {
    intent,
    clients,
    isLoading,
    isError,
    onSelectPartner,
  };
}
